import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useOptionalSupportHub } from '../support-hub/SupportHubContext';
import { extractInvokeErrorDetails, mapExpertChatError } from './useExpertChat';
import type {
    FlowAnswerPayload,
    FlowNode,
    FlowRun,
    FlowStepResponse,
    FlowTerminal,
} from './types';

function useSafeUserId(): string | null {
    try {
        const auth = useAuth();
        return auth?.user?.id ?? null;
    } catch {
        return null;
    }
}

export async function resolveFlowInvokeErrorMessage(
    err: unknown,
    data?: unknown,
    fallback = 'Request failed.',
): Promise<string> {
    const details = await extractInvokeErrorDetails(err, data);
    const mapped = mapExpertChatError(details.rawMessage, details.code, details.status);
    if (
        details.rawMessage &&
        details.rawMessage.trim() &&
        details.rawMessage.trim() !== 'Edge Function returned a non-2xx status code'
    ) {
        return details.rawMessage.trim();
    }
    return mapped.message || fallback;
}

export interface UseTroubleshootingFlowOptions {
    sessionId?: string | null;
}

export function useTroubleshootingFlow({ sessionId }: UseTroubleshootingFlowOptions = {}) {
    const hub = useOptionalSupportHub();
    const currentUserId = useSafeUserId();
    const currentUserIdRef = useRef<string | null>(currentUserId);
    currentUserIdRef.current = currentUserId;
    const attemptedResumeRunIdsRef = useRef<Set<string>>(new Set());

    const [localFlowRun, setLocalFlowRun] = useState<FlowRun | null>(null);
    const [localFlowNode, setLocalFlowNode] = useState<FlowNode | null>(null);
    const [localFlowTerminal, setLocalFlowTerminal] = useState<FlowTerminal | null>(null);
    const [localFlowTitle, setLocalFlowTitle] = useState<string | null>(null);
    const [localFlowError, setLocalFlowError] = useState<string | null>(null);
    const [localNeedsClarification, setLocalNeedsClarification] = useState(false);
    const [isFlowLoading, setIsFlowLoading] = useState(false);

    const activeFlowRun = hub?.activeFlowRun !== undefined ? hub.activeFlowRun : localFlowRun;
    const setActiveFlowRun = hub?.setActiveFlowRun ?? setLocalFlowRun;

    const activeFlowNode = hub?.activeFlowNode !== undefined ? hub.activeFlowNode : localFlowNode;
    const setActiveFlowNode = hub?.setActiveFlowNode ?? setLocalFlowNode;

    const activeFlowTerminal = hub?.activeFlowTerminal !== undefined ? hub.activeFlowTerminal : localFlowTerminal;
    const setActiveFlowTerminal = hub?.setActiveFlowTerminal ?? setLocalFlowTerminal;

    const flowTitle = hub?.flowTitle !== undefined ? hub.flowTitle : localFlowTitle;
    const setFlowTitle = hub?.setFlowTitle ?? setLocalFlowTitle;

    const flowError = hub?.flowError !== undefined ? hub.flowError : localFlowError;
    const setFlowError = hub?.setFlowError ?? setLocalFlowError;

    const needsClarification = hub?.needsClarification !== undefined ? hub.needsClarification : localNeedsClarification;
    const setNeedsClarification = hub?.setNeedsClarification ?? setLocalNeedsClarification;

    const activeFlowRunRef = useRef<FlowRun | null>(null);
    activeFlowRunRef.current = activeFlowRun;
    const flowTitleRef = useRef<string | null>(null);
    flowTitleRef.current = flowTitle;

    const undismissFlowId = useCallback(
        (flowId?: string | null) => {
            if (!flowId || !hub?.setDismissedFlowIds) return;
            hub.setDismissedFlowIds((prev) => {
                if (!prev.has(flowId)) return prev;
                const next = new Set(prev);
                next.delete(flowId);
                return next;
            });
        },
        [hub],
    );

    const applyFlowState = useCallback(
        (data: FlowStepResponse | null | undefined, titleOverride?: string | null) => {
            if (!data) return false;
            const effectiveTitle = titleOverride ?? flowTitleRef.current;
            if (data.run) {
                const runWithTitle = {
                    ...data.run,
                    flow_title: data.run.flow_title || effectiveTitle || undefined,
                    title: data.run.title || effectiveTitle || undefined,
                };
                setActiveFlowRun(runWithTitle);
                activeFlowRunRef.current = runWithTitle;
            }
            if (data.terminal) {
                setActiveFlowTerminal(data.terminal);
                setActiveFlowNode(null);
                undismissFlowId(data.run?.flow_id ?? activeFlowRunRef.current?.flow_id);
                return true;
            }
            if (data.node) {
                setActiveFlowNode(data.node);
                setActiveFlowTerminal(null);
                return true;
            }
            return false;
        },
        [setActiveFlowNode, setActiveFlowRun, setActiveFlowTerminal, undismissFlowId],
    );

    // M1: Resume run via flow_state if needed (active run present but neither node nor terminal)
    useEffect(() => {
        const run = activeFlowRun;
        if (
            run &&
            run.status === 'ACTIVE' &&
            !activeFlowNode &&
            !activeFlowTerminal &&
            !isFlowLoading &&
            !attemptedResumeRunIdsRef.current.has(run.id)
        ) {
            attemptedResumeRunIdsRef.current.add(run.id);
            const callerUserId = currentUserIdRef.current;
            let mounted = true;
            void (async () => {
                try {
                    const { data, error } = await supabase.functions.invoke('expert-chat', {
                        body: {
                            action: 'flow_state',
                            run_id: run.id,
                        },
                    });
                    if (!mounted || currentUserIdRef.current !== callerUserId) return;
                    if (!error && data) {
                        applyFlowState(data as FlowStepResponse);
                    }
                } catch {
                    /* ignore resume error */
                }
            })();
            return () => {
                mounted = false;
            };
        }
    }, [activeFlowNode, activeFlowRun, activeFlowTerminal, applyFlowState, isFlowLoading]);

    const startFlow = useCallback(
        async (flowId: string, title?: string): Promise<FlowStepResponse | null> => {
            // M1: Never start a second run while one is ACTIVE
            if (activeFlowRunRef.current && activeFlowRunRef.current.status === 'ACTIVE') {
                return null;
            }

            const sid = sessionId;
            if (!sid) {
                setFlowError('No active expert session to start troubleshooting flow.');
                return null;
            }

            const callerUserId = currentUserIdRef.current;

            // Contract: resolve title from suggestion/offer if not passed directly
            const resolvedTitle =
                title ??
                hub?.flowOffers?.find((o) => o.flow_id === flowId)?.title ??
                (hub?.flowOffer?.flow_id === flowId ? hub.flowOffer.title : null);
            if (resolvedTitle) {
                setFlowTitle(resolvedTitle);
            }

            setIsFlowLoading(true);
            setFlowError(null);
            try {
                const { data, error } = await supabase.functions.invoke('expert-chat', {
                    body: {
                        action: 'flow_start',
                        session_id: sid,
                        flow_id: flowId,
                    },
                });

                if (currentUserIdRef.current !== callerUserId) {
                    return null;
                }

                if (error) {
                    const msg = await resolveFlowInvokeErrorMessage(
                        error,
                        data,
                        'Failed to start flow',
                    );
                    if (currentUserIdRef.current !== callerUserId) return null;
                    setFlowError(msg);
                    return null;
                }

                const res = data as FlowStepResponse;
                applyFlowState(res, resolvedTitle);

                // M2: Starting a flow consumes that offer
                hub?.consumeFlowOffer?.(flowId);

                return res;
            } catch (err: unknown) {
                if (currentUserIdRef.current !== callerUserId) return null;
                const msg = await resolveFlowInvokeErrorMessage(err, undefined, 'Could not start flow.');
                if (currentUserIdRef.current !== callerUserId) return null;
                setFlowError(msg);
                return null;
            } finally {
                if (currentUserIdRef.current === callerUserId) {
                    setIsFlowLoading(false);
                }
            }
        },
        [applyFlowState, hub, sessionId, setFlowError, setFlowTitle],
    );

    const answerFlow = useCallback(
        async (payload: FlowAnswerPayload): Promise<FlowStepResponse | null> => {
            const runId = payload.run_id ?? activeFlowRunRef.current?.id;
            if (!runId) return null;

            const callerUserId = currentUserIdRef.current;

            setIsFlowLoading(true);
            setFlowError(null);
            try {
                const reqPayload: Record<string, unknown> = {
                    action: 'flow_answer',
                    run_id: runId,
                    node_id: payload.node_id,
                };
                if (payload.option_id !== undefined) reqPayload.option_id = payload.option_id;
                if (payload.answer_text !== undefined) reqPayload.answer_text = payload.answer_text;
                if (payload.reading !== undefined) reqPayload.reading = payload.reading;
                if (payload.image_path !== undefined) reqPayload.image_path = payload.image_path;

                const { data, error } = await supabase.functions.invoke('expert-chat', {
                    body: reqPayload,
                });

                if (currentUserIdRef.current !== callerUserId) {
                    return null;
                }

                if (error) {
                    // Check for stale node error or body with latest node
                    const errAny = error as unknown as {
                        context?: {
                            json?: () => Promise<unknown>;
                            text?: () => Promise<string>;
                            clone?: () => { json?: () => Promise<unknown>; text?: () => Promise<string> };
                        };
                    };
                    const ctx = errAny?.context;
                    if (ctx) {
                        try {
                            const reader = typeof ctx.clone === 'function' ? ctx.clone() : ctx;
                            let errBody: FlowStepResponse | null = null;
                            if (typeof reader.json === 'function') {
                                errBody = (await reader.json()) as FlowStepResponse;
                            } else if (typeof reader.text === 'function') {
                                const raw = await reader.text();
                                if (raw) errBody = JSON.parse(raw) as FlowStepResponse;
                            }
                            if (currentUserIdRef.current !== callerUserId) return null;
                            if (errBody?.node || errBody?.terminal) {
                                applyFlowState(errBody);
                                return errBody;
                            }
                        } catch {
                            /* ignore json parse error */
                        }
                    }
                    const msg = await resolveFlowInvokeErrorMessage(
                        error,
                        data,
                        'Failed to submit answer',
                    );
                    if (currentUserIdRef.current !== callerUserId) return null;
                    setFlowError(msg);
                    return null;
                }

                const res = data as FlowStepResponse;
                applyFlowState(res);
                setNeedsClarification(Boolean(res.needs_clarification));
                return res;
            } catch (err: unknown) {
                if (currentUserIdRef.current !== callerUserId) return null;
                const msg = await resolveFlowInvokeErrorMessage(
                    err,
                    undefined,
                    'Could not submit answer.',
                );
                if (currentUserIdRef.current !== callerUserId) return null;
                setFlowError(msg);
                return null;
            } finally {
                if (currentUserIdRef.current === callerUserId) {
                    setIsFlowLoading(false);
                }
            }
        },
        [applyFlowState, setFlowError, setNeedsClarification],
    );

    const stepBackFlow = useCallback(async (): Promise<FlowStepResponse | null> => {
        const runId = activeFlowRunRef.current?.id;
        if (!runId) return null;

        const callerUserId = currentUserIdRef.current;

        setIsFlowLoading(true);
        setFlowError(null);
        try {
            const { data, error } = await supabase.functions.invoke('expert-chat', {
                body: {
                    action: 'flow_back',
                    run_id: runId,
                },
            });

            if (currentUserIdRef.current !== callerUserId) {
                return null;
            }

            if (error) {
                const msg = await resolveFlowInvokeErrorMessage(
                    error,
                    data,
                    'Failed to step back',
                );
                if (currentUserIdRef.current !== callerUserId) return null;
                setFlowError(msg);
                return null;
            }

            const res = data as FlowStepResponse;
            applyFlowState(res);
            return res;
        } catch (err: unknown) {
            if (currentUserIdRef.current !== callerUserId) return null;
            const msg = await resolveFlowInvokeErrorMessage(err, undefined, 'Could not step back.');
            if (currentUserIdRef.current !== callerUserId) return null;
            setFlowError(msg);
            return null;
        } finally {
            if (currentUserIdRef.current === callerUserId) {
                setIsFlowLoading(false);
            }
        }
    }, [applyFlowState, setFlowError]);

    const dismissFlow = useCallback(() => {
        const currentFlowId = activeFlowRunRef.current?.flow_id;
        if (currentFlowId) {
            undismissFlowId(currentFlowId);
        }
        setActiveFlowRun(null);
        activeFlowRunRef.current = null;
        setActiveFlowNode(null);
        setActiveFlowTerminal(null);
        setFlowTitle(null);
        setFlowError(null);
        setNeedsClarification(false);
    }, [
        setActiveFlowNode,
        setActiveFlowRun,
        setActiveFlowTerminal,
        setFlowError,
        setFlowTitle,
        setNeedsClarification,
        undismissFlowId,
    ]);

    const abandonFlow = useCallback(async (): Promise<boolean> => {
        const callerUserId = currentUserIdRef.current;
        const runId = activeFlowRunRef.current?.id;
        const currentStatus = activeFlowRunRef.current?.status;
        dismissFlow();

        if (!runId) return true;
        if (currentStatus === 'COMPLETED') return true;

        try {
            await supabase.functions.invoke('expert-chat', {
                body: {
                    action: 'flow_abandon',
                    run_id: runId,
                },
            });
            if (currentUserIdRef.current !== callerUserId) return false;
            return true;
        } catch {
            return false;
        }
    }, [dismissFlow]);

    const fetchFlowSummary = useCallback(async (): Promise<string | null> => {
        const callerUserId = currentUserIdRef.current;
        const runId = activeFlowRunRef.current?.id;
        if (!runId) return null;

        try {
            const { data } = await supabase.functions.invoke('expert-chat', {
                body: {
                    action: 'flow_summary',
                    run_id: runId,
                },
            });
            if (currentUserIdRef.current !== callerUserId) return null;
            return (data as { summary_md?: string } | null)?.summary_md ?? null;
        } catch {
            return null;
        }
    }, []);

    return {
        activeFlowRun,
        activeFlowNode,
        activeFlowTerminal,
        flowTitle,
        isFlowLoading,
        flowError,
        needsClarification,
        startFlow,
        answerFlow,
        stepBackFlow,
        abandonFlow,
        dismissFlow,
        fetchFlowSummary,
    };
}
